import { ToPlainText } from '@empathy/global/functions';

import { surveyQuestionTextFields } from './survey-question-text-fields';
import { BlockLoopSourceEnum } from '../models/block-loop-source.enum';
import { BlockModel } from '../models/block.model';
import { Command } from '../models/command.type';
import { Condition } from '../models/condition.model';
import { Question } from '../models/question.model';
import { LoopSourceReference } from '../models/survey-loop.interface';

/** Shared by participant resolution and editor selection; the template is never mutated. */
export function materializeLoopQuestion(
  template: Question,
  loopQuestions: Question[],
  loop: BlockModel,
  source: LoopSourceReference,
  pageId?: string
): Question {
  const question = JSON.parse(JSON.stringify(template)) as Question;
  // Answer identity stays compact; the code identifies the loop iteration to readers.
  question.id = `${template.id}_${source.sourceIndex}`;
  question.code = `${template.code}__Loop${source.sourceIndex}`;
  question.pageId = pageId ?? `${template.pageId}_${source.sourceIndex}`;
  rewriteLoopContent(question, loopQuestions, source);
  appendLoopMedia(question, template.id, loop, source);
  return question;
}

function rewriteLoopContent(question: Question, loopQuestions: Question[], source: LoopSourceReference): void {
  const loopQuestionIds = loopQuestions.map((template) => template.id);
  question.visibleWhen = rewriteLoopConditions(question.visibleWhen, loopQuestionIds, source.sourceIndex);

  for (const field of surveyQuestionTextFields) {
    if (question[field] !== undefined) {
      question[field] = rewriteLoopText(question[field], loopQuestions, source);
    }
  }

  const items = [...(question.options ?? []), ...(question.cards ?? []), ...(question.ranks ?? [])];
  for (const item of items) {
    item.text = rewriteLoopText(item.text, loopQuestions, source);
    item.visibleWhen = rewriteLoopConditions(item.visibleWhen, loopQuestionIds, source.sourceIndex);
  }

  for (const option of question.options ?? []) {
    option.selectedWhen = rewriteLoopConditions(option.selectedWhen, loopQuestionIds, source.sourceIndex);
  }
}

const rewriteLoopConditions = (conditions: (Condition | Command)[], loopQuestionIds: string[], sourceIndex: number): (Condition | Command)[] =>
  conditions?.map((condition) => {
    if (typeof condition === 'string') {
      return condition;
    }

    const isLocalReference = loopQuestionIds.includes(condition.questionId);
    const questionId = isLocalReference ? `${condition.questionId}_${sourceIndex}` : condition.questionId;
    return { ...condition, questionId };
  });

function rewriteLoopText(text: string, loopQuestions: Question[], source: LoopSourceReference): string {
  const textWithSourceName = text?.replace(/\[loopOption\]/g, () => ToPlainText(source.sourceName));

  // Only local template references get this source's suffix; explicit instance references stay unchanged.
  return textWithSourceName?.replace(/\[([^\]|]+)([^\]]*)\]/g, (match, reference: string, argumentsText: string) => {
    const questionReference = reference.trim();
    const localQuestion = loopQuestions.find((question) => question.id === questionReference || question.code === questionReference);
    if (!localQuestion) {
      return match;
    }

    const instanceCode = `${localQuestion.code}__Loop${source.sourceIndex}`;
    const pipeArguments = argumentsText.replace(/\s*\|\s*loopVar\s*$/, '');
    return `[${instanceCode}${pipeArguments}]`;
  });
}

function appendLoopMedia(question: Question, templateQuestionId: string, loop: BlockModel, source: LoopSourceReference): void {
  const receivesLoopMedia = loop.sourceType === BlockLoopSourceEnum.Media && loop.stimulusQuestionIds?.includes(templateQuestionId);
  if (!receivesLoopMedia) {
    return;
  }

  const media = loop.sourceMedia.find((item) => item.mediaId === source.sourceId);
  const mediaCopy = JSON.parse(JSON.stringify(media));
  question.stimulus = [...(question.stimulus ?? []), mediaCopy];
}
